import { useCallback, useEffect, useState } from 'react';

import { api } from '../api/endpoints';
import type {
  AdminStats,
  EventFunnel,
  Participant,
  RegistrationStatus,
  StatsPeriod,
} from '../api/types';
import { STATUS_LABELS } from '../api/types';
import { formatEventDate, formatIdr } from '../format';

const PERIODS: { key: StatsPeriod; label: string }[] = [
  { key: 'upcoming', label: 'Впереди' },
  { key: 'past', label: 'Прошли' },
  { key: 'all', label: 'Всё' },
];

/** Куда админ может перевести человека вручную. Порядок — это путь
 *  по воронке, поэтому кнопки читаются слева направо как шаги. */
const ACTIONS: { status: RegistrationStatus; label: string }[] = [
  { status: 'confirmed', label: 'Подтвердил' },
  { status: 'awaiting_payment', label: 'Ждёт оплаты' },
  { status: 'paid', label: 'Оплатил' },
  { status: 'attended', label: 'Пришёл' },
  { status: 'cancelled', label: 'Отменил' },
];

export function AdminPage() {
  const [period, setPeriod] = useState<StatsPeriod>('upcoming');
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [openEvent, setOpenEvent] = useState<EventFunnel | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .stats(period)
      .then((data) => {
        setStats(data);
        setError(null);
      })
      .catch(() => setError('Не удалось загрузить статистику.'));
  }, [period]);

  useEffect(load, [load]);

  if (openEvent) {
    return (
      <EventParticipants
        event={openEvent}
        onBack={() => {
          setOpenEvent(null);
          // Статусы могли измениться — воронка должна это показать.
          load();
        }}
      />
    );
  }

  if (error) {
    return (
      <div className="screen">
        <h1 className="screen__title">Статистика</h1>
        <p className="muted">{error}</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="screen">
        <h1 className="screen__title">Статистика</h1>
        <div className="skeleton" />
      </div>
    );
  }

  const top = stats.funnel[0]?.count ?? 0;
  const { money, people } = stats;

  return (
    <div className="screen">
      <h1 className="screen__title">Статистика</h1>

      <div className="segmented">
        {PERIODS.map((item) => (
          <button
            key={item.key}
            className={`segmented__item${period === item.key ? ' segmented__item--on' : ''}`}
            onClick={() => setPeriod(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <section className="block">
        <h2 className="block__title">Воронка</h2>
        <p className="muted">
          На каждой ступени — сколько человек до неё дошло. Проценты: от числа
          записавшихся и от предыдущего шага.
        </p>

        {stats.funnel.map((stage) => (
          <div key={stage.key} className="funnel">
            <div className="funnel__head">
              <span className="funnel__label">{stage.label}</span>
              <span className="funnel__count">{stage.count}</span>
            </div>
            <div className="funnel__track">
              <div
                className="funnel__bar"
                style={{ width: `${top ? (stage.count / top) * 100 : 0}%` }}
              />
            </div>
            <div className="funnel__note">
              {stage.from_top}% от записавшихся
              {stage.from_prev !== null && ` · ${stage.from_prev}% от предыдущего`}
            </div>
          </div>
        ))}

        {stats.cancelled > 0 && (
          <p className="muted small">
            Отменили: {stats.cancelled}. На каком шаге отвалились — не видно:
            хранится текущий статус, а не история переходов.
          </p>
        )}
      </section>

      <section className="block">
        <h2 className="block__title">Деньги</h2>
        <dl className="facts">
          <Fact label="Получено" value={formatIdr(money.collected_idr)} strong />
          <Fact label="Ждёт подтверждения" value={formatIdr(money.awaiting_idr)} />
          <Fact label="В работе" value={formatIdr(money.potential_idr)} />
          <Fact label="Упущено на отменах" value={formatIdr(money.lost_idr)} />
        </dl>
        <p className="muted small">
          Считается по цене мероприятия. Когда появится оплата бонусами,
          источником станут сами платежи.
        </p>
      </section>

      {stats.stuck.length > 0 && (
        <section className="block">
          <h2 className="block__title">Зависли</h2>
          <p className="muted">
            Сказали «приду» больше суток назад и не двинулись дальше. Это список
            для обзвона, а не отчёт.
          </p>
          {stats.stuck.map((row) => (
            <div key={row.registration_id} className="stuck">
              <div className="stuck__name">{row.full_name}</div>
              <div className="muted small">
                {row.event_title} · {formatEventDate(row.starts_at)}
              </div>
              <div className="muted small">
                {STATUS_LABELS[row.status]} · ждём {row.waiting_hours} ч ·{' '}
                {formatIdr(row.amount_idr)}
              </div>
            </div>
          ))}
        </section>
      )}

      {stats.by_category.length > 0 && (
        <section className="block">
          <h2 className="block__title">По направлениям</h2>
          <p className="muted">Где воронка течёт — общая цифра это скрывает.</p>
          <table className="grid">
            <thead>
              <tr>
                <th>Направление</th>
                <th>Записей</th>
                <th>Оплат</th>
                <th>Деньги</th>
              </tr>
            </thead>
            <tbody>
              {stats.by_category.map((row) => (
                <tr key={row.code}>
                  <td>{row.name}</td>
                  <td>{row.signed_up}</td>
                  <td>
                    {row.paid}
                    {row.signed_up > 0 && (
                      <span className="muted">
                        {' '}
                        ({Math.round((row.paid / row.signed_up) * 100)}%)
                      </span>
                    )}
                  </td>
                  <td>{row.collected_idr.toLocaleString('ru-RU')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {stats.events.length > 0 && (
        <section className="block">
          <h2 className="block__title">По мероприятиям</h2>
          <p className="muted">Нажмите, чтобы увидеть поимённо и сменить статус.</p>
          {stats.events.map((event) => (
            <button
              key={event.event_id}
              className="eventrow"
              onClick={() => setOpenEvent(event)}
            >
              <div className="eventrow__title">{event.title}</div>
              <div className="muted small">
                {formatEventDate(event.starts_at)}
                {event.fill_percent !== null && ` · заполнено ${event.fill_percent}%`}
              </div>
              <div className="eventrow__counts">
                {(Object.keys(STATUS_LABELS) as RegistrationStatus[])
                  .filter((key) => key !== 'cancelled' && event.counts[key])
                  .map((key) => (
                    <span key={key} className="chip">
                      {STATUS_LABELS[key]}: {event.counts[key]}
                    </span>
                  ))}
                {event.cancelled > 0 && (
                  <span className="chip">Отменили: {event.cancelled}</span>
                )}
              </div>
              {event.collected_idr > 0 && (
                <div className="eventrow__money">{formatIdr(event.collected_idr)}</div>
              )}
            </button>
          ))}
        </section>
      )}

      <section className="block">
        <h2 className="block__title">Люди</h2>
        <dl className="facts">
          <Fact label="Всего" value={String(people.total)} strong />
          <Fact
            label="Заполнили анкету"
            value={`${people.with_questionnaire}${
              people.total ? ` (${Math.round((people.with_questionnaire / people.total) * 100)}%)` : ''
            }`}
          />
          <Fact label="Новых за неделю" value={String(people.new_this_week)} />
          <Fact label="Платили хоть раз" value={String(people.paying)} />
          <Fact
            label="Платили не раз"
            value={`${people.repeat_paying}${
              people.paying ? ` (${Math.round((people.repeat_paying / people.paying) * 100)}%)` : ''
            }`}
          />
          <Fact label="Заблокировали бота" value={String(people.bot_blocked)} />
        </dl>
        <p className="muted small">
          Повторная оплата — единственная метрика здесь про сообщество, а не про
          поток: разовый интерес можно купить рекламой, второй приход — нет.
        </p>
      </section>
    </div>
  );
}

function Fact({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="fact">
      <dt>{label}</dt>
      <dd className={strong ? 'fact__strong' : undefined}>{value}</dd>
    </div>
  );
}

function EventParticipants({
  event,
  onBack,
}: {
  event: EventFunnel;
  onBack: () => void;
}) {
  const [people, setPeople] = useState<Participant[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  useEffect(() => {
    api
      .eventRegistrations(event.event_id)
      .then(setPeople)
      .catch(() => setPeople([]));
  }, [event.event_id]);

  async function move(registrationId: number, status: RegistrationStatus) {
    setBusy(registrationId);
    try {
      const updated = await api.setRegistrationStatus(registrationId, status);
      setPeople((current) =>
        (current ?? []).map((p) =>
          p.registration_id === registrationId ? updated : p,
        ),
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="screen">
      <button className="back" onClick={onBack}>
        ← Назад
      </button>
      <h1 className="screen__title">{event.title}</h1>
      <p className="muted">
        {formatEventDate(event.starts_at)} · {formatIdr(event.price_idr)}
      </p>

      {people === null && <div className="skeleton" />}
      {people !== null && people.length === 0 && (
        <p className="muted">Пока никто не записался.</p>
      )}

      {people?.map((person) => (
        <div key={person.registration_id} className="participant">
          <div className="participant__name">{person.full_name}</div>
          {person.occupation && (
            <div className="muted small">{person.occupation}</div>
          )}
          <div className="muted small">
            Сейчас: {STATUS_LABELS[person.status]}
          </div>
          <div className="picks">
            {ACTIONS.map((action) => (
              <button
                key={action.status}
                type="button"
                className={`pick${person.status === action.status ? ' pick--on' : ''}`}
                disabled={busy === person.registration_id}
                onClick={() => move(person.registration_id, action.status)}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      ))}

      <p className="muted small">
        «Оплатил» подтверждает платёж: пишется строка в платежи и в журнал
        действий, а человеку уходит уведомление.
      </p>
    </div>
  );
}
